import { apiInstance } from '@/data/api/api.config';
import { STORAGE_KEYS } from '@/data/constants/storage';
import { toUser } from '@/data/dto/user.dto';
import { USER_STORAGE, User, UserHttp } from '@/data/models/user.interface';
import { storageService } from '@/data/services/storage.service';
import useUserStore from '@/data/stores/user.store';
import {
  cleanScudTokenFromUrl,
  extractScudTokenFromUrl,
} from '@/utils/auth/url.utils';

const REDIRECT_ATTEMPT_KEY = 'AUTH_REDIRECT_ATTEMPTS';

class AuthService {
  public TOKEN_SESSION_NAME = STORAGE_KEYS.AUTH_TOKEN;
  private storage = storageService;
  private authEndpoint = '/me';
  private skipAuth = process.env.VITE_APP_SKIP_AUTH === 'true';
  private authErrorListeners: Set<() => void> = new Set();

  public getToken(): string | null {
    const envToken = process.env.VITE_APP_BEARER_TOKEN;
    return envToken || this.storage.get<string>(this.TOKEN_SESSION_NAME);
  }

  public setToken(token: string): void {
    return this.storage.set(this.TOKEN_SESSION_NAME, token);
  }

  public hasTokenInUrl(): boolean {
    const currentUrl = window.location.toString();
    return currentUrl.includes('scud_api_token=');
  }

  public initializeToken(): void {
    // Skip URL token processing if bearer token is set in env
    if (process.env.VITE_APP_BEARER_TOKEN) {
      console.log('🔑 Using bearer token from environment');
      return;
    }

    const currentUrl = window.location.toString();
    const apiToken = extractScudTokenFromUrl(currentUrl);

    if (apiToken) {
      console.log('🔑 Processing SCUD token from URL...');
      this.setToken(apiToken);

      this.clearRedirectAttempts();

      const cleanUrl = cleanScudTokenFromUrl(currentUrl);
      console.log('✅ Token stored, redirecting to clean URL:', cleanUrl);

      // Use replace instead of href to avoid adding to history
      window.location.replace(cleanUrl);
      return;
    }

    console.log('ℹ️ No token found in URL');
  }

  public clearAllAuthData(): void {
    this.removeToken();
    this.clearRedirectAttempts();
    this.clearAuthError();
    this.storage.remove(USER_STORAGE);
    // Clear redirect attempts from localStorage too
    localStorage.removeItem('AUTH_REDIRECT_ATTEMPTS');
    localStorage.removeItem('LAST_REDIRECT_ATTEMPT');
    // Clear login state from sessionStorage (both old and new keys)
    sessionStorage.removeItem(STORAGE_KEYS.LOGIN_STATE_IN_PROGRESS);
  }

  private clearRedirectAttempts(): void {
    this.storage.remove(REDIRECT_ATTEMPT_KEY);
    this.storage.remove('LAST_REDIRECT_ATTEMPT');
  }

  public async getMe(): Promise<User> {
    console.log('🔍 getMe called');

    if (this.skipAuth) {
      console.log('🔓 Skipping auth, returning mock user');
      return this.getMockUser();
    }

    const token = this.getToken();
    const userState = useUserStore.getState();
    const isLoggingIn = userState.user.isLoggingIn;

    if (!token) {
      console.log('❌ No token available, returning empty user');
      return toUser({});
    }

    if (token && !isLoggingIn) {
      console.log('🔄 Token found after refresh, setting isLoggingIn to true');
      userState.setIsLoggingIn(true);
    }

    try {
      console.log('🌐 Making API call to /auth/me');
      const response = await apiInstance.get<{ data: UserHttp }>(
        this.authEndpoint,
      );

      const user = toUser(response.data?.data || {});
      this.storage.set(USER_STORAGE, response.data?.data || {});
      this.clearRedirectAttempts();
      this.clearAuthError();

      console.log('✅ Authentication successful:', user.fullName);
      return user;
    } catch (error) {
      console.error('❌ Authentication failed:', error);

      const axiosError = error as {
        response?: { status?: number };
      };

      // If token is invalid/expired (401), clear it so AuthGuard can redirect properly
      if (axiosError?.response?.status === 401) {
        console.log('🔄 Token expired/invalid (401), clearing token');
        this.removeToken();
        userState.setIsLoggingIn(false);
        // Don't set auth error for 401 - it's expected when token expires
        return toUser({});
      }

      // For other errors, store and display them
      this.setAuthError(error);
      this.removeToken();
      return toUser({});
    }
  }

  public setAuthError(error: unknown): void {
    const axiosError = error as {
      response?: {
        data?: { message?: string };
        status?: number;
      };
      config?: {
        url?: string;
        method?: string;
        baseURL?: string;
      };
      message?: string;
    };

    const errorInfo = {
      message:
        axiosError?.response?.data?.message ||
        axiosError?.message ||
        'Unknown authentication error',
      status: axiosError?.response?.status || 0,
      timestamp: Date.now(),
      origin: {
        url: axiosError?.config?.url || 'unknown',
        method: axiosError?.config?.method?.toUpperCase() || 'GET',
        baseURL: axiosError?.config?.baseURL || '',
      },
    };
    this.storage.set(STORAGE_KEYS.AUTH_ERROR, errorInfo);
    // Notify all listeners about the auth error
    this.notifyAuthErrorListeners();
  }

  public getAuthError(): {
    message: string;
    status: number;
    timestamp: number;
    origin: {
      url: string;
      method: string;
      baseURL: string;
    };
  } | null {
    return this.storage.get(STORAGE_KEYS.AUTH_ERROR);
  }

  public clearAuthError(): void {
    this.storage.remove(STORAGE_KEYS.AUTH_ERROR);
    // Notify listeners that error is cleared
    this.notifyAuthErrorListeners();
  }

  private getMockUser(): User {
    console.log('🔍 getMockUser called');
    if (this.skipAuth) {
      return toUser({
        type: 'manager_repositories',
        id: 'dev-user',
        attributes: {
          id: 1,
          avatar: '',
          createdAt: new Date().toISOString(),
          email: 'dev@example.com',
          first_name: 'Dev',
          last_name: 'User',
          hierarchy: 1,
          initials: 'DEV',
          last_connection: new Date().toISOString(),
          other_role: null,
          position: 1,
          role: 1,
          job: 'Developer',
          permissions: ['view_dashboard', 'manage_users', 'view_reports'],
          groups: [],
          service: 'Development',
          title: 'Developer',
          manager_repository_id: 'dev-manager',
          regions: ['FR'],
          updatedAt: new Date().toISOString(),
          emulated_by: null,
        },
      });
    }
    return toUser({});
  }

  public hasPermissionsTo(guard: string): boolean {
    const { user } = useUserStore.getState();
    return user?.permissions?.includes(guard) || false;
  }

  public removeToken(): void {
    this.storage.remove(this.TOKEN_SESSION_NAME);
    this.storage.remove(USER_STORAGE);
  }

  public logout(): void {
    const userState = useUserStore.getState();
    userState.setIsLoggingIn(false);

    this.removeToken();
  }

  public async checkAuth(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return false;

    try {
      await apiInstance.get(this.authEndpoint);
      return true;
    } catch {
      return false;
    }
  }

  // Auth error listener management
  public addAuthErrorListener(callback: () => void): () => void {
    this.authErrorListeners.add(callback);
    // Return unsubscribe function
    return () => this.authErrorListeners.delete(callback);
  }

  private notifyAuthErrorListeners(): void {
    this.authErrorListeners.forEach((callback) => callback());
  }
}

export default new AuthService();
